import { extendTheme } from "@chakra-ui/react";

const breakpoints = {
  base: "0px",
  sm: "320px",
  md: "768px",
  lg: "960px",
  xl: "1350px",
  "2xl": "1536px",
  "3xl": "1636px",
};

const colors = {
  brand: {
    50: "#EFF6FF",
    100: "#DBEAFE",
    200: "#BFDBFE",
    300: "#93C5FD",
    400: "#60A5FA",
    500: "#2563EB",
    600: "#1D4ED8",
    700: "#1E40AF",
    800: "#1E3A8A",
    900: "#0A1628",
  },
  accent: {
    400: "#22D3EE",
    500: "#06B6D4",
    600: "#0891B2",
  },
  gold: {
    400: "#FBBF24",
    500: "#F59E0B",
    600: "#D97706",
  },
  ink: "#0A1628",
  slate: {
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172B",
  },
  // Legacy tokens kept for backwards compatibility
  blueBg: "#2563EB",
  blueB: "#1D4ED8",
  blue2: "#0A1628",
  darkBg: "#0A1628",
  orangeBg: "#F59E0B",
  semiBlue: "#EFF6FF",
};

export const customTheme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
  fonts: {
    heading: `'Plus Jakarta Sans', 'DM Sans', system-ui, -apple-system, sans-serif`,
    body: `'Inter', 'DM Sans', system-ui, -apple-system, sans-serif`,
  },
  breakpoints,
  colors,
  semanticTokens: {
    colors: {
      pageBg: { default: "#FFFFFF", _dark: "#050C1A" },
      surface: { default: "#FFFFFF", _dark: "#0F172B" },
      surfaceAlt: { default: "slate.50", _dark: "#0A1628" },
      surfaceBorder: { default: "slate.100", _dark: "rgba(255,255,255,0.08)" },
      textPrimary: { default: "ink", _dark: "#F1F5F9" },
      textMuted: { default: "slate.500", _dark: "slate.400" },
    },
  },
  styles: {
    global: (props) => ({
      "html, body": {
        bg: props.colorMode === "dark" ? "#050C1A" : "#FFFFFF",
        color: props.colorMode === "dark" ? "#F1F5F9" : "ink",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        transition: "background-color .35s ease, color .35s ease",
      },
      "*": { boxSizing: "border-box" },
      "::selection": { background: "rgba(37, 99, 235, 0.18)" },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        borderRadius: "12px",
        transition: "all .25s cubic-bezier(.4,0,.2,1)",
      },
      variants: {
        primary: {
          bgGradient: "linear(135deg, brand.500 0%, accent.500 100%)",
          color: "#fff",
          boxShadow: "0 10px 30px -10px rgba(37,99,235,0.55)",
          _hover: {
            transform: "translateY(-2px)",
            boxShadow: "0 18px 40px -12px rgba(37,99,235,0.65)",
            _disabled: { transform: "none" },
          },
          _active: { transform: "translateY(0)" },
        },
        ghostLine: {
          bg: "transparent",
          color: "ink",
          border: "1.5px solid",
          borderColor: "slate.200",
          _hover: {
            borderColor: "brand.500",
            color: "brand.500",
            transform: "translateY(-2px)",
          },
        },
        glass: {
          bg: "rgba(255,255,255,0.12)",
          color: "#fff",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.25)",
          _hover: {
            bg: "rgba(255,255,255,0.22)",
            transform: "translateY(-2px)",
          },
        },
        // Hero-only accent — use ONCE per page for the primary conversion.
        // Warm gold breaks the sea-of-blue CTAs and creates real hierarchy.
        gold: {
          bgGradient: "linear(135deg, gold.500 0%, gold.400 100%)",
          color: "#0A1628",
          boxShadow: "0 10px 30px -10px rgba(245,158,11,0.55)",
          _hover: {
            transform: "translateY(-2px)",
            boxShadow: "0 20px 40px -12px rgba(245,158,11,0.7)",
            _disabled: { transform: "none" },
          },
          _active: { transform: "translateY(0)" },
        },
      },
      defaultProps: { variant: "primary" },
    },
    Input: {
      variants: {
        modern: {
          field: {
            bg: "white",
            border: "1.5px solid",
            borderColor: "slate.200",
            borderRadius: "12px",
            _hover: { borderColor: "slate.300" },
            _focus: {
              borderColor: "brand.500",
              boxShadow: "0 0 0 4px rgba(37,99,235,0.12)",
            },
            _placeholder: { color: "slate.400" },
          },
        },
      },
      defaultProps: { variant: "modern" },
    },
    Textarea: {
      variants: {
        modern: {
          bg: "white",
          border: "1.5px solid",
          borderColor: "slate.200",
          borderRadius: "12px",
          _hover: { borderColor: "slate.300" },
          _focus: {
            borderColor: "brand.500",
            boxShadow: "0 0 0 4px rgba(37,99,235,0.12)",
          },
          _placeholder: { color: "slate.400" },
        },
      },
      defaultProps: { variant: "modern" },
    },
  },
});
